import React, { useEffect, useState } from "react";
import {
  TbBuildingBank,
  TbCheck,
  TbChevronDown,
  TbClock,
  TbCode,
  TbCopy,
  TbCreditCard,
  TbDiscount,
  TbReceipt,
  TbShieldCheck,
} from "react-icons/tb";

const asObject = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const prettyLabel = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const normaliseMethod = (value) => {
  const method = String(value || "").toLowerCase();

  if (method.includes("upi")) return "upi";
  if (method.includes("card")) return "card";
  if (!method || method === "cashfree") return "unknown";
  return method;
};

export const formatPaymentMethod = (value) => {
  const method = normaliseMethod(value);
  return method === "unknown" ? "Not captured" : prettyLabel(method);
};

const formatDateTime = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DetailGrid = ({ data }) => {
  const entries = Object.entries(asObject(data)).filter(
    ([, value]) => value !== null && value !== undefined && value !== ""
  );

  if (!entries.length) return null;

  return (
    <dl className="payment-detail-grid">
      {entries.map(([key, value]) => (
        <div className="payment-detail-row" key={key}>
          <dt>{prettyLabel(key)}</dt>
          <dd>{displayValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

const CopyableValue = ({ value, copyKey, copiedKey, onCopy }) => (
  <div className="payment-copy-value">
    <strong>{value || "Not available"}</strong>
    {value && (
      <button
        type="button"
        aria-label={`Copy ${prettyLabel(copyKey)}`}
        title={`Copy ${prettyLabel(copyKey)}`}
        onClick={() => onCopy(copyKey, value)}
      >
        {copiedKey === copyKey ? <TbCheck /> : <TbCopy />}
      </button>
    )}
  </div>
);

const PaymentGatewayDetails = ({ payment, orderPaymentMethod }) => {
  const [copiedKey, setCopiedKey] = useState("");
  const [developerView, setDeveloperView] = useState(false);

  useEffect(() => {
    setDeveloperView(false);
  }, [payment?.payment_id]);

  if (!payment) {
    return (
      <section className="payment-gateway-panel payment-gateway-empty">
        <TbReceipt />
        <div>
          <h3>Payment details unavailable</h3>
          <p>No gateway record is attached to this order.</p>
        </div>
      </section>
    );
  }

  const payload = asObject(payment.webhook_payload);
  const payloadData = asObject(payload.data);
  const paymentEntity = asObject(payloadData.payment || payload.payment);
  const methodPayload = asObject(paymentEntity.payment_method);
  const rawMethodKey = Object.keys(methodPayload)[0];
  const method = normaliseMethod(
    payment.method || orderPaymentMethod || rawMethodKey || payment.payment_group
  );
  const methodDetails = asObject(
    rawMethodKey ? methodPayload[rawMethodKey] : methodPayload
  );
  const gatewayDetails = asObject(payloadData.payment_gateway_details);
  const customerDetails = asObject(payloadData.customer_details);
  const surcharge = asObject(paymentEntity.payment_surcharge);
  const terminalDetails = asObject(payloadData.terminal_details);
  const offers = Array.isArray(payloadData.payment_offers)
    ? payloadData.payment_offers
    : [];
  const events = Array.isArray(payment.cashfree_events)
    ? payment.cashfree_events
    : [];
  const gatewayName = payment.gateway || gatewayDetails.gateway_name || "cashfree";
  const paymentAmount = paymentEntity.payment_amount ?? payment.amount;
  const paymentCurrency = paymentEntity.payment_currency || payment.currency || "INR";
  const paymentStatus = paymentEntity.payment_status || payment.status;
  const paymentId = payment.cashfree_payment_id || paymentEntity.cf_payment_id;
  const gatewayOrderId = payment.cashfree_order_id || payloadData.order?.order_id;
  const bankReference = paymentEntity.bank_reference;
  const instrumentNumber =
    methodDetails.card_number || methodDetails.upi_id || methodDetails.vpa;
  const instrumentBank = methodDetails.card_bank_name || methodDetails.bank_name;
  const instrumentNetwork = methodDetails.card_network || methodDetails.channel;
  const cardDigits = String(methodDetails.card_number || "").replace(/\D/g, "");
  const cardLastFour = cardDigits.slice(-4) || "XXXX";
  const maskedCardNumber = `XXXX XXXX XXXX ${cardLastFour}`;
  const cardholderName = customerDetails.customer_name || "Cardholder";
  const hasTechnicalDetails =
    Object.keys(methodDetails).length > 0 ||
    offers.length > 0 ||
    Object.keys(surcharge).length > 0 ||
    Object.keys(customerDetails).length > 0 ||
    Object.keys(gatewayDetails).length > 0 ||
    Object.keys(terminalDetails).length > 0 ||
    events.length > 0 ||
    Object.keys(payload).length > 0;

  const copyValue = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 1600);
    } catch {
      setCopiedKey("");
    }
  };

  return (
    <section className="payment-gateway-panel">
      <header className="payment-gateway-heading">
        <div className="payment-method-icon">
          {method === "card" ? <TbCreditCard /> : <TbBuildingBank />}
        </div>

        <div className="payment-heading-copy">
          <span>Payment</span>
          <h3>{formatPaymentMethod(method)}</h3>
          <p>
            {prettyLabel(payment.payment_group || method)} via {prettyLabel(gatewayName)}
          </p>
        </div>

        <div className="payment-amount-block">
          <span>Amount paid</span>
          <strong>
            {paymentCurrency} {Number(paymentAmount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </strong>
          <b className={`payment-status-pill ${String(paymentStatus || "").toLowerCase()}`}>
            {prettyLabel(paymentStatus || "Stored")}
          </b>
        </div>
      </header>

      <div className="payment-summary-grid">
        <div>
          <span>Paid on</span>
          <strong>{formatDateTime(paymentEntity.payment_time)}</strong>
        </div>
        <div>
          <span>Payment ID</span>
          <CopyableValue
            value={paymentId}
            copyKey="payment_id"
            copiedKey={copiedKey}
            onCopy={copyValue}
          />
        </div>
        <div>
          <span>Gateway order</span>
          <CopyableValue
            value={gatewayOrderId}
            copyKey="gateway_order_id"
            copiedKey={copiedKey}
            onCopy={copyValue}
          />
        </div>
      </div>

      {method === "card" && (
        <div className="payment-instrument-showcase">
          <div className="payment-card-visual">
            <div className="payment-card-brand-row">
              <span>{instrumentBank || "Bank card"}</span>
              <strong>{String(instrumentNetwork || "CARD").toUpperCase()}</strong>
            </div>
            <span className="payment-card-chip" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <div className="payment-card-number">{maskedCardNumber}</div>
            <div className="payment-card-footer">
              <span>
                <small>Card holder</small>
                <strong>{cardholderName}</strong>
              </span>
              <span>
                <small>Type</small>
                <strong>{prettyLabel(methodDetails.card_type || payment.payment_group)}</strong>
              </span>
            </div>
          </div>

          <div className="payment-card-facts">
            <div>
              <span>Ending in</span>
              <strong>{cardLastFour}</strong>
            </div>
            <div>
              <span>Network</span>
              <strong>{prettyLabel(instrumentNetwork || "Card")}</strong>
            </div>
            <div>
              <span>Issuing bank</span>
              <strong>{instrumentBank ? prettyLabel(instrumentBank) : "Not available"}</strong>
            </div>
          </div>
        </div>
      )}

      {method !== "card" && (instrumentNumber || instrumentNetwork || instrumentBank) && (
        <div className="payment-instrument-strip">
          <TbCreditCard />
          <div>
            <span>{method === "upi" ? "UPI account" : "Payment instrument"}</span>
            <strong>{instrumentNumber || formatPaymentMethod(method)}</strong>
          </div>
          {instrumentNetwork && <span>{prettyLabel(instrumentNetwork)}</span>}
          {instrumentBank && <span>{prettyLabel(instrumentBank)}</span>}
        </div>
      )}

      {hasTechnicalDetails && (
        <details className="payment-more-details">
          <summary>
            <div>
              <TbReceipt />
              <span>
                <strong>More payment details</strong>
                <small>Bank reference, instrument data and offers</small>
              </span>
            </div>
            <TbChevronDown className="payment-details-chevron" />
          </summary>

          <div className="payment-more-content">
            <div className="payment-reference-row">
              <span>Bank reference</span>
              <CopyableValue
                value={bankReference}
                copyKey="bank_reference"
                copiedKey={copiedKey}
                onCopy={copyValue}
              />
            </div>

            {Object.keys(methodDetails).length > 0 && (
              <div className="payment-detail-section">
                <div className="payment-detail-title">
                  <TbCreditCard />
                  <h4>{formatPaymentMethod(method)} details</h4>
                </div>
                <DetailGrid data={methodDetails} />
              </div>
            )}

            {offers.length > 0 && (
              <div className="payment-detail-section">
                <div className="payment-detail-title">
                  <TbDiscount />
                  <h4>Applied offers</h4>
                </div>
                <div className="payment-offer-list">
                  {offers.map((offer, index) => {
                    const meta = asObject(offer.offer_meta);
                    const redemption = asObject(offer.offer_redemption);

                    return (
                      <div className="payment-offer-card" key={offer.offer_id || index}>
                        <strong>{meta.offer_title || offer.offer_id || "Cashfree offer"}</strong>
                        <span>{meta.offer_description || prettyLabel(offer.offer_type)}</span>
                        <small>
                          {meta.offer_code ? `${meta.offer_code} · ` : ""}
                          Discount INR {Number(redemption.discount_amount || 0).toFixed(2)}
                        </small>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {Object.keys(surcharge).length > 0 && (
              <div className="payment-detail-section">
                <div className="payment-detail-title">
                  <TbReceipt />
                  <h4>Surcharge</h4>
                </div>
                <DetailGrid data={surcharge} />
              </div>
            )}

            <div className="payment-secondary-disclosures">
              {Object.keys(customerDetails).length > 0 && (
                <details className="payment-data-disclosure">
                  <summary><TbShieldCheck /> Customer data from gateway</summary>
                  <DetailGrid data={customerDetails} />
                </details>
              )}
            </div>

            <div className="developer-view-control">
              <div>
                <TbCode />
                <span>
                  <strong>Developer view</strong>
                  <small>Gateway response, webhook history and raw payloads</small>
                </span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={developerView}
                className={developerView ? "active" : ""}
                onClick={() => setDeveloperView((current) => !current)}
              >
                <span />
                {developerView ? "On" : "Off"}
              </button>
            </div>

            {developerView && (
              <div className="payment-developer-panel">
                <div className="payment-developer-heading">
                  <TbCode />
                  <div>
                    <strong>Developer data</strong>
                    <span>Technical Cashfree records for troubleshooting.</span>
                  </div>
                </div>

                <div className="payment-secondary-disclosures">
                  {Object.keys(gatewayDetails).length > 0 && (
                    <details className="payment-data-disclosure">
                      <summary><TbBuildingBank /> Gateway response</summary>
                      <DetailGrid data={gatewayDetails} />
                    </details>
                  )}

                  {Object.keys(terminalDetails).length > 0 && (
                    <details className="payment-data-disclosure">
                      <summary><TbReceipt /> Terminal details</summary>
                      <DetailGrid data={terminalDetails} />
                    </details>
                  )}
                </div>

                <div className="payment-event-section">
                  <div className="payment-detail-title">
                    <TbClock />
                    <h4>Gateway event history</h4>
                    <span>{events.length} stored</span>
                  </div>

                  {events.length > 0 ? (
                    <div className="payment-event-list">
                      {events.map((event) => (
                        <details className="payment-event" key={event.webhook_event_id}>
                          <summary>
                            <span>
                              <strong>{prettyLabel(event.event_type)}</strong>
                              <small>{formatDateTime(event.event_time || event.createdAt)}</small>
                            </span>
                            <b>{prettyLabel(event.payment_status || "Stored")}</b>
                          </summary>
                          <pre>{JSON.stringify(asObject(event.payload), null, 2)}</pre>
                        </details>
                      ))}
                    </div>
                  ) : (
                    <p className="payment-event-empty">
                      No webhook events have been recorded for this historical payment.
                    </p>
                  )}
                </div>

                {Object.keys(payload).length > 0 && (
                  <details className="payment-raw-payload">
                    <summary><TbCode /> Latest stored Cashfree payload</summary>
                    <pre>{JSON.stringify(payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            )}
          </div>
        </details>
      )}
    </section>
  );
};

export default PaymentGatewayDetails;
